import { zodResolver } from "@hookform/resolvers/zod";
import { HelpCircle, Home, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import type {
  CurrencyFieldConfig,
  ImagesFieldConfig,
  NumberFieldConfig,
  PercentageFieldConfig,
  SelectFieldConfig,
  SwitchFieldConfig,
  TextFieldConfig,
  TextareaFieldConfig,
} from "@/lib/form-config";
import { PROPERTY_TYPE_LABELS } from "@/schemas/journey";
import {
  PROPERTY_STATUS,
  type Property,
  propertySchema,
} from "@/schemas/property";

import { ManualPropertyImagesUpload } from "@/components/app/properties/manual/manual-property-images-upload";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useJourneyData } from "@/hooks/use-journey-data";
import { deleteFile } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { useState } from "react";

type FormFields = Pick<
  Property,
  | "status"
  | "address"
  | "city"
  | "state"
  | "zip"
  | "price"
  | "bedrooms"
  | "bathrooms"
  | "square_feet"
  | "lot_size"
  | "year_built"
  | "property_type"
  | "description"
  | "images"
  | "listing_agent_name"
  | "listing_agent_phone"
  | "listing_agent_email"
>;

type FieldConfiguration =
  | TextFieldConfig
  | SelectFieldConfig
  | CurrencyFieldConfig
  | PercentageFieldConfig
  | SwitchFieldConfig
  | TextareaFieldConfig
  | NumberFieldConfig
  | ImagesFieldConfig;

const fieldConfig: Record<keyof FormFields, FieldConfiguration> = {
  status: {
    label: "Status",
    description: "The current status of the property",
    type: "select",
    options: Object.entries(PROPERTY_STATUS).map(([value, label]) => ({
      value,
      label,
    })),
    required: true,
  },
  address: {
    label: "Address",
    description: "The address of the property",
    type: "text",
    required: true,
    placeholder: "123 Main Street",
  },
  city: {
    label: "City",
    description: "The city of the property",
    type: "text",
    required: true,
    placeholder: "San Francisco",
  },
  state: {
    label: "State",
    description: "The state of the property",
    type: "text",
    required: true,
    placeholder: "CA",
  },
  zip: {
    label: "Zip",
    description: "The zip code of the property",
    type: "text",
    required: true,
    placeholder: "94105",
  },
  price: {
    label: "Price",
    description: "The price of the property",
    type: "currency",
    required: true,
    placeholder: "500000",
  },
  bedrooms: {
    label: "Bedrooms",
    description: "The number of bedrooms in the property",
    type: "number",
    placeholder: "3",
  },
  bathrooms: {
    label: "Bathrooms",
    description: "The number of bathrooms in the property",
    type: "number",
    placeholder: "2",
  },
  square_feet: {
    label: "Square Feet",
    description: "The square footage of the property",
    type: "number",
    placeholder: "2000",
  },
  lot_size: {
    label: "Lot Size",
    description: "The size of the lot in square feet",
    type: "number",
    placeholder: "5000",
  },
  year_built: {
    label: "Year Built",
    description: "The year the property was built",
    type: "number",
    placeholder: "1990",
  },
  property_type: {
    label: "Property Type",
    description: "The type of property",
    placeholder: "Select property type",
    type: "select",
    options: Object.entries(PROPERTY_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
    required: true,
  },
  description: {
    label: "Description",
    description: "A description of the property",
    type: "textarea",
    placeholder: "Describe the key features and condition of the property...",
  },
  images: {
    label: "Images",
    description: "The images of the property",
    type: "images",
  },
  listing_agent_name: {
    label: "Listing Agent Name",
    description: "The name of the listing agent",
    type: "text",
    placeholder: "John Smith",
  },
  listing_agent_phone: {
    label: "Listing Agent Phone",
    description: "The phone number of the listing agent",
    type: "text",
    placeholder: "(555) 123-4567",
  },
  listing_agent_email: {
    label: "Listing Agent Email",
    description: "The email of the listing agent",
    type: "text",
    placeholder: "agent@example.com",
  },
};

type FieldConfig = typeof fieldConfig;
type FieldName = keyof FieldConfig;

// Define an interface for the section type
interface FormSection {
  title: string;
  description: string;
  fields: readonly (keyof FormFields)[];
}

// Update the FORM_SECTIONS constant with the proper type
const FORM_SECTIONS: readonly FormSection[] = [
  {
    title: "Basic Information",
    description: "Essential property location and status details",
    fields: ["status", "address", "city", "state", "zip"],
  },
  {
    title: "Property Details",
    description: "Core property characteristics and specifications",
    fields: [
      "price",
      "property_type",
      "square_feet",
      "bedrooms",
      "bathrooms",
      "year_built",
    ],
  },
  {
    title: "Additional Details",
    description: "Extra property information and features",
    fields: ["lot_size", "description", "images"],
  },
  {
    title: "Listing Agent Information",
    description: "Contact details for the property's listing agent",
    fields: [
      "listing_agent_name",
      "listing_agent_phone",
      "listing_agent_email",
    ],
  },
] as const;

export const ManualPropertyAdditionForm = ({
  onComplete,
}: { onComplete: () => void }) => {
  const { createPropertyManually, journeyData } = useJourneyData();

  if (!journeyData?.id) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto space-y-6">
          <div className="bg-primary/5 rounded-2xl p-8 mx-auto">
            <div className="bg-primary/10 rounded-full p-5 w-20 h-20 mx-auto flex items-center justify-center">
              <Home className="w-10 h-10 text-primary" />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              No Active Journey
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              You need to create or select a journey before adding properties.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const form = useForm<Property>({
    resolver: zodResolver(
      propertySchema.omit({
        id: true,
        url: true,
        source: true,
        last_scraped_at: true,
        listed_at: true,
        latitude: true,
        longitude: true,
        created_at: true,
        updated_at: true,
        property_data_json: true,
        is_cron: true,
        neighborhood: true,
      }),
    ),
    defaultValues: {
      status: "active",
      address: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
      price: 0,
      bedrooms: null,
      bathrooms: null,
      square_feet: null,
      lot_size: null,
      year_built: null,
      property_type: "townhome",
      description: null,
      images: null,
      listing_agent_name: null,
      listing_agent_phone: null,
      listing_agent_email: null,
    },
    mode: "onChange",
  });

  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const imagePathsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      if (imagePathsRef.current.length > 0) {
        void cleanupUploadedFiles();
      }
    };
  }, []);

  useEffect(() => {
    imagePathsRef.current = imagePaths;
  }, [imagePaths]);

  const cleanupUploadedFiles = async () => {
    if (imagePathsRef.current.length > 0) {
      const removeFilePromises = imagePathsRef.current.map((path) =>
        deleteFile("properties_media", path),
      );
      await Promise.all(removeFilePromises);
      setImagePaths([]);
    }
  };

  const onSubmit = async (data: Property) => {
    try {
      await createPropertyManually.mutateAsync({
        ...data,
      });
      setImagePaths([]);
      onComplete();
    } catch (error) {
      await cleanupUploadedFiles();
      throw error;
    }
  };

  const handleCancel = async () => {
    await cleanupUploadedFiles();
    onComplete();
  };

  const renderField = (fieldName: keyof Property) => {
    const config = fieldConfig[fieldName as FieldName];
    if (!config) return null;

    return (
      <FormField
        key={fieldName}
        control={form.control}
        name={fieldName}
        render={({ field }) => (
          <FormItem className="space-y-3 h-full flex flex-col">
            <div className="space-y-0.5">
              <div className="flex items-center justify-between">
                <FormLabel>{config.label}</FormLabel>
                {!config.required && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                    Optional
                  </span>
                )}
              </div>
              <FormDescription>{config.description}</FormDescription>
            </div>
            <FormControl>
              {config.type === "select" ? (
                <Select
                  value={field.value?.toString() ?? ""}
                  onValueChange={(value) => {
                    const numericValue = config.options.some(
                      (opt) => typeof opt.value === "number",
                    )
                      ? Number(value)
                      : value;
                    field.onChange(numericValue);
                  }}
                  required={config.required}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={config.placeholder}>
                      {field.value &&
                        config.options.find(
                          (opt) =>
                            opt.value.toString() === field.value?.toString(),
                        )?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {config.options.map(({ value, label, description }) => (
                      <SelectItem
                        key={value.toString()}
                        value={value.toString()}
                      >
                        <div>
                          <div>{label}</div>
                          {description && (
                            <div className="text-xs text-muted-foreground">
                              {description}
                            </div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : config.type === "textarea" ? (
                <Textarea
                  {...field}
                  required={config.required}
                  value={field.value?.toString() ?? ""}
                  placeholder={config.placeholder}
                />
              ) : config.type === "currency" ? (
                <FormattedNumberInput
                  {...field}
                  required={config.required}
                  value={typeof field.value === "number" ? field.value : null}
                  onChange={(value) => field.onChange(value)}
                  prefix="$"
                  placeholder={config.placeholder}
                  maxDecimals={0}
                />
              ) : config.type === "percentage" ? (
                <FormattedNumberInput
                  {...field}
                  required={config.required}
                  value={typeof field.value === "number" ? field.value : null}
                  onChange={(value) => field.onChange(value)}
                  suffix="%"
                  placeholder={config.placeholder}
                  allowDecimal
                  maxDecimals={3}
                />
              ) : config.type === "images" ? (
                <ManualPropertyImagesUpload
                  onChange={async (images: { url: string; path: string }[]) => {
                    const newImagePaths = images.map((image) => image.path);
                    const removedPaths = imagePathsRef.current.filter(
                      (path) => !newImagePaths.includes(path),
                    );
                    if (removedPaths.length > 0) {
                      const removeFilePromises = removedPaths.map((path) =>
                        deleteFile("properties_media", path),
                      );
                      await Promise.all(removeFilePromises);
                    }

                    field.onChange(
                      images.length > 0
                        ? images.map((image) => image.url)
                        : null,
                    );
                    setImagePaths(newImagePaths);
                  }}
                />
              ) : config.type === "number" ? (
                <Input
                  {...field}
                  required={config.required}
                  type="number"
                  value={(field.value ?? "").toString()}
                  onChange={(e) => {
                    const value = e.target.value
                      ? Number(e.target.value)
                      : null;
                    field.onChange(value);
                  }}
                  placeholder={config.placeholder}
                  className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              ) : (
                <Input
                  {...field}
                  required={config.required}
                  value={field.value?.toString() ?? ""}
                  placeholder={config.placeholder}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {FORM_SECTIONS.map((section) => (
            <div key={section.title} className="space-y-6">
              <div>
                <h3 className="text-base font-semibold flex items-center gap-2">
                  {section.title}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 p-0"
                      >
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{section.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {section.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
                {section.fields.map((field) => (
                  <div
                    key={field}
                    className={cn(
                      "flex flex-col",
                      field === "images" && "md:col-span-2",
                    )}
                  >
                    {renderField(field as keyof Property)}
                  </div>
                ))}
              </div>

              <Separator />
            </div>
          ))}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                form.formState.isSubmitting
              }
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Add Property"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
